// Formatting helpers. Money from the API is integer cents.
import i18n from './i18n';

let currency = 'USD';
export const setCurrency = (c: string) => { currency = c || 'USD'; };

// Arabic pages show amounts as "$1,234.50" kept left-to-right inside the right-to-left text (U+2066…U+2069 isolate them)
const rtl = () => i18n.language === 'ar';
const numLocale = () => (rtl() ? 'en-US' : i18n.language);
const iso = (s: string) => (rtl() ? `\u2066${s}\u2069` : s);

const nf = new Map<string, Intl.NumberFormat>();
function moneyFmt(cur: string) {
  const key = numLocale() + cur;
  if (!nf.has(key)) {
    try { nf.set(key, new Intl.NumberFormat(numLocale(), { style: 'currency', currency: cur })); }
    catch { nf.set(key, new Intl.NumberFormat(numLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })); }
  }
  return nf.get(key)!;
}

export const money = (cents: number | null | undefined, cur = currency) => iso(moneyFmt(cur).format((Number(cents) || 0) / 100));
export const moneyUnits = (amount: number, cur = currency) => iso(moneyFmt(cur).format(amount || 0));
export const num = (n: number | null | undefined, digits = 0) =>
  n == null || Number.isNaN(Number(n)) ? '—' : iso(new Intl.NumberFormat(numLocale(), { maximumFractionDigits: digits }).format(Number(n)));

function toDate(s: string | Date) {
  if (s instanceof Date) return s;
  return new Date(s.length === 10 ? s + 'T00:00:00' : s);
}
// Some browsers ship without Uzbek date data (they print "2026 M10 7"); format it by hand there
const UZ_MONTHS = ['yan', 'fev', 'mar', 'apr', 'may', 'iyn', 'iyl', 'avg', 'sen', 'okt', 'noy', 'dek'];
let uzBroken: boolean | null = null;
const manualUz = () => {
  if (i18n.language !== 'uz') return false;
  // Missing locale data shows up either as "unsupported" or as placeholder months like "M10"
  if (uzBroken === null) {
    try { uzBroken = !Intl.DateTimeFormat.supportedLocalesOf('uz').length || /M\d/.test(new Date(2026, 9, 7).toLocaleDateString('uz', { month: 'short' })); }
    catch { uzBroken = true; }
  }
  return uzBroken;
};
const pad = (n: number) => String(n).padStart(2, '0');
// Some browsers have no month names for Bosnian, Tajik or Somali (they print "M11"): use the closest language they do have
const DATE_FALLBACK: Record<string, string> = { bs: 'sr-Latn', tg: 'ru', so: 'en' };
const dateOk = new Map<string, boolean>();
/** The locale to format dates with in the current UI language */
export function dateLocale() {
  const l = i18n.language;
  if (!DATE_FALLBACK[l]) return l;
  if (!dateOk.has(l)) {
    try { dateOk.set(l, Intl.DateTimeFormat.supportedLocalesOf(l).length > 0 && !/M\d/.test(new Date(2026, 9, 7).toLocaleDateString(l, { month: 'short' }))); }
    catch { dateOk.set(l, false); }
  }
  return dateOk.get(l) ? l : DATE_FALLBACK[l]!;
}
/** Short month name ("okt", "окт.", "Eki") */
export const monthShort = (d: Date) => (manualUz() ? UZ_MONTHS[d.getMonth()]! : d.toLocaleDateString(dateLocale(), { month: 'short' }));
export const date = (s?: string | Date | null) => {
  if (!s) return '—';
  const d = toDate(s);
  if (manualUz()) return `${d.getDate()}-${UZ_MONTHS[d.getMonth()]}, ${d.getFullYear()}`;
  return d.toLocaleDateString(dateLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
};
export const dateTime = (s?: string | Date | null) => {
  if (!s) return '—';
  const d = toDate(s);
  if (manualUz()) return `${d.getDate()}-${UZ_MONTHS[d.getMonth()]}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return d.toLocaleString(dateLocale(), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const today = () => new Date().toISOString().slice(0, 10);
export const daysAgo = (d: number) => new Date(Date.now() - d * 864e5).toISOString().slice(0, 10);
export const monthStart = () => today().slice(0, 8) + '01';

// Form inputs work in currency units; the API wants cents
export const toCents = (v: number | null | undefined) => (v == null ? undefined : Math.round(Number(v) * 100));
export const fromCents = (c: number | null | undefined) => (c == null ? '' : (Number(c) / 100).toFixed(2));

const UZ_MONTHS_LONG = ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr'];
const UZ_DAYS = ['yakshanba', 'dushanba', 'seshanba', 'chorshanba', 'payshanba', 'juma', 'shanba'];
/** "Wednesday, November 4" in the UI language (browsers lack Uzbek month names, so it is spelled out here) */
export function longToday(d = new Date()) {
  if (i18n.language === 'uz') return `${d.getDate()}-${UZ_MONTHS_LONG[d.getMonth()]}, ${UZ_DAYS[d.getDay()]}`;
  try { return new Intl.DateTimeFormat(dateLocale(), { weekday: 'long', day: 'numeric', month: 'long' }).format(d); } catch { return d.toDateString(); }
}
